import { X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { formatDateTime, formatHourMin } from "@/lib/format";
import { useT } from "@/lib/i18n";

/**
 * F20 — pinned-window scope chip.
 *
 * Reads the time-range params from the URL exactly as useUrlTimeRange does
 * and renders a small sibling chip in PageShell's title row, but ONLY when a
 * CUSTOM absolute window is pinned (`preset === "custom"` with a finite,
 * forward start/end). Relative presets (live-1h … ytd) are the page default
 * and stay invisible — the chip is ~95%-invisible by design, so it never adds
 * chrome to an ordinary glance page.
 *
 * The clear affordance deletes start/end/gran/preset, which makes
 * useUrlTimeRange fall back to its defaultPreset (i.e. restores the page
 * default). The breadcrumb-trail idea was rejected per the finding.
 */
export function ScopeChip() {
  const [params, setParams] = useSearchParams();
  const t = useT();

  const preset = params.get("preset");
  const start = Number(params.get("start"));
  const end = Number(params.get("end"));
  const gran = params.get("gran");

  // Gate: only a pinned custom window shows the chip. Anything else (the
  // common relative-preset default) renders nothing.
  if (!(preset === "custom" && Number.isFinite(start) && Number.isFinite(end) && end > start)) {
    return null;
  }

  function clear() {
    const next = new URLSearchParams(params);
    next.delete("start");
    next.delete("end");
    next.delete("gran");
    next.delete("preset");
    setParams(next, { replace: true });
  }

  const label =
    `${t("monitor.shell.scope")}: ${formatDateTime(start)} → ${formatHourMin(end)}` +
    (gran ? ` · ${t("analyze.granLabel." + gran)}` : "");

  return (
    <button
      type="button"
      onClick={clear}
      title={t("monitor.shell.scopeClear")}
      className="hidden items-center gap-1 rounded border border-edge bg-surface px-1.5 py-0.5 text-[10px] text-text-muted hover:text-text md:inline-flex"
    >
      <span>{label}</span>
      <X size={11} />
    </button>
  );
}
